import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntersect } from '@de/hooks/useIntersect';
import type { CustomDimensions, Step } from '@de/types/types';
import type { DynamicExperienceEventContext } from '@de/events/buildEventContext';
import {
  addFiredKey,
  buildEntryPointKey,
  hasFiredKey,
} from '@de/utils/firedEntryEventKeys';
import {
  applyProductContext,
  buildFlowContext,
  getEntryPointConfigFromSteps,
  resolveConfiguredCustomDimensions,
} from '@de/utils/configurable-step';
import type {
  CustomDimensionMapping,
  ProductFieldMapping,
} from '@de/utils/configurable-step';
import type { FlowState } from './dynamicExperienceReducer';
// Type-only import — the hook never invokes the provider at runtime,
// it just receives the `triggers` map by value. `import type` keeps
// the dependency graph one-directional at runtime even though the
// type lives alongside the provider that consumes this hook.
import type { TrackingTriggers } from './TrackingProvider';

export interface UseEntryPointEventFiringArgs {
  /**
   * Inherited event context from `DynamicExperienceProvider`.
   * `undefined` when the `TrackingProvider` is mounted outside a
   * `DynamicExperienceProvider`; the hook becomes a no-op in that
   * case (no triggers called, no observer mounted).
   */
  readonly eventContext: DynamicExperienceEventContext | undefined;
  /**
   * Current step from the surrounding DE context. Drives both the
   * mount-fire branch (entry vs non-entry) and the dedup-key shape.
   */
  readonly currentStep: Step | undefined;
  /**
   * Trigger surface from `useFlowTrackingEvents`. The hook calls:
   * - entry mount: `triggerElementLoadedEvent` then
   *   `triggerProductLoadedEvent` (§6.1)
   * - non-entry mount: `triggerStepElementLoadedEvent` — wire name
   *   `ElementLoaded` with the step-scoped webElement shape from
   *   `createStepElementLoadedPayload` (§7.3)
   * - entry intersect: `triggerProductViewedEvent` then
   *   `triggerFormViewedEvent` (§6.1)
   * - non-entry intersect: `triggerStepElementViewedEvent` — wire
   *   name `ElementViewed` with the step-scoped webElement shape from
   *   `createStepElementViewedPayload` (§7.3)
   */
  readonly triggers: TrackingTriggers;
  /**
   * Optional DSL config id (from `TrackingProvider.dslConfigId`).
   * Used as the dedup-scope fallback when `currentStep.flowId` is
   * absent — see {@link buildEntryPointKey}.
   */
  readonly dslConfigId?: string;
  /**
   * Published config version. Configured events read it from step
   * correlation. Built-in entry Product events do not.
   */
  readonly dslConfigVersion?: number;
  /**
   * Per-provider-session identifier from
   * `DynamicExperienceProvider`. Threaded into the entry-point
   * dedup key so a brand-new mount of the same flow in the same tab
   * (SPA nav, widget reopen, parent re-key) emits the lifecycle
   * sequence again instead of being suppressed by keys stamped
   * during a prior session. `undefined` falls back to the legacy
   * unscoped key shape.
   */
  readonly flowSessionId?: string;
  // `true` when the entry's own content declares product surfaces (EL/result
  // cards), including placements with `_meta.tracking.enabled: false`. Those
  // nodes claim the Product surface even when they emit nothing, so the
  // entry unit must NOT also fire a unit-level Product. Derived statically
  // via {@link contentDeclaresProductSurface}. This is currently the only
  // gate on the unit Product, preserving parity for DCS-widget/question entries.
  readonly contentEmitsProducts?: boolean;
  // Field-composition summary (`fieldCount`/`fieldNames`/`fieldSet`) from
  // {@link buildEntryFieldSummary}, merged by key onto the entry unit's Product
  // events so a generic DSL-entry Product still reports what it contains.
  // Empty for non-field entries (purely additive).
  readonly entryFieldDimensions?: CustomDimensions;
  /**
   * Dual-shape `tracking.productFields` from the current step's DSL
   * root. Applied onto ProductLoaded / ProductViewed at fire time
   * against live `flowContext`. Missing/empty = today's dcs/presets.
   */
  readonly productFields?: Readonly<Record<string, ProductFieldMapping>>;
  /**
   * Sibling `tracking.customDimensions` mappings. Resolved against
   * live `flowContext` at fire time and passed as
   * `mappedCustomDimensions` so payload builders overlay them.
   */
  readonly customDimensionMappings?: readonly CustomDimensionMapping[];
  /**
   * Live DE `flowState` used to rebuild `flowContext` at fire time.
   * Held in a ref so keystroke updates do not re-fire Loaded/Viewed.
   */
  readonly flowState?: FlowState;
  /**
   * Flow steps for {@link buildFlowContext} / subject-label fallback.
   */
  readonly steps?: Step[];
}

export interface UseEntryPointEventFiringResult {
  /**
   * Ref callback the host wires to the step's DOM root. Stable
   * identity across renders (underlying state held in a `useRef` +
   * `useState` pair) so re-renders of the host do NOT tear down and
   * re-mount the `IntersectionObserver`.
   *
   * StrictMode contract: called with `(node, null, node)` under the
   * dev-time double-effect. The callback treats repeated calls with
   * the same node as a no-op; a non-null call after a null call
   * re-attaches the observer.
   */
  readonly setEntryRootNode: (node: HTMLElement | null) => void;
}

/**
 * Owns step-level lifecycle event firing for the DSL render path.
 * Mounted unconditionally by `TrackingProvider`; gates internally on
 * `currentStep` shape so unrelated mounts (no DE context, missing
 * step) cleanly no-op.
 *
 * **Fires on mount:**
 * - `currentStep.isEntry === true`: `ElementLoaded` then
 *   `ProductLoaded` (Confluence §6.1 ordering — entry-point chain
 *   with the app-shell webElement shape from
 *   `createElementLoadedPayload`).
 * - `currentStep.isEntry !== true`: `ElementLoaded` (Confluence §7.3
 *   — step-scoped webElement shape from
 *   `createStepElementLoadedPayload`; the JS trigger is still
 *   named `triggerStepElementLoadedEvent` for historical reasons but
 *   the on-the-wire event name is `ElementLoaded`).
 *
 * **Fires on the first viewport intersect** of the attached entry
 * root (via `setEntryRootNode`):
 * - `currentStep.isEntry === true`: `ProductViewed` then
 *   `FormViewed` (§6.1 ordering).
 * - `currentStep.isEntry !== true`: `ElementViewed` (§7.3 — emitted
 *   via `triggerStepElementViewedEvent`, same naming caveat as the
 *   mount trigger above). Non-entry steps do NOT fire `FormViewed`;
 *   `FormViewed` is reserved for the entry step that introduces the
 *   form context (§6.1 / §9.2).
 *
 * Every fire is gated on the shared
 * {@link firedEntryEventKeys} Set keyed by
 * `entryPoint:<wireEventName>:<flowId|dslConfigId>:<stepId>:<flowSessionId>`
 * — the event-name segment is the on-the-wire name (`ElementLoaded`,
 * `ElementViewed`, ...) so dedup keys stay legible against the
 * Tagular event stream. The `flowSessionId` segment isolates each
 * provider-mount session so a fresh mount of the same flow does not
 * inherit suppression from a prior session. Within a single session,
 * revisiting the same step (back button, parent re-key without a
 * hard nav) does not re-fire.
 *
 * The observer is disconnected synchronously inside the intersect
 * handler (mirrors `Trackable.tsx`'s pattern); we do not rely on
 * `setNodes([])` for tear-down — under React 18 Strict Mode that
 * pattern can re-attach and re-fire.
 */
export const useEntryPointEventFiring = (
  args: UseEntryPointEventFiringArgs,
): UseEntryPointEventFiringResult => {
  const {
    eventContext,
    currentStep,
    triggers,
    dslConfigId,
    flowSessionId,
    contentEmitsProducts,
    entryFieldDimensions,
    productFields,
    customDimensionMappings,
    flowState,
    steps,
  } = args;

  const isActive = currentStep !== undefined && eventContext !== undefined;
  const isEntryStep = currentStep?.isEntry === true;

  // The unit fires its Product lifecycle only when its content carries no products.
  const unitFiresProduct = contentEmitsProducts !== true;

  // Live resolve inputs sit on a ref so form keystrokes (flowState)
  // update the next fire without re-running mount/intersect effects.
  const liveProductApplyRef = useRef({
    productFields,
    customDimensionMappings,
    flowState,
    steps,
    currentStep,
  });
  liveProductApplyRef.current = {
    productFields,
    customDimensionMappings,
    flowState,
    steps,
    currentStep,
  };

  /**
   * Merge field-composition dims with the mapped `product` overlay
   * resolved from live `flowContext` at fire time.
   *
   * @param {{ customDimensions: CustomDimensions } | undefined} dims - Field dims
   * @returns {Record<string, unknown> | undefined} Trigger override
   */
  const buildEntryProductOverride = (
    dims: { customDimensions: CustomDimensions } | undefined,
  ): Record<string, unknown> | undefined => {
    const live = liveProductApplyRef.current;
    const subjectLabelFallback = getEntryPointConfigFromSteps(
      live.steps ?? [],
    )?.subjectLabelFallback;
    const flowContext = buildFlowContext(
      live.flowState,
      live.steps,
      live.currentStep,
      { subjectLabelFallback },
    );
    const product = applyProductContext({
      map: live.productFields,
      root: flowContext,
    });
    const mappedCustomDimensions = resolveConfiguredCustomDimensions(
      live.customDimensionMappings,
      flowContext,
    );
    if (!dims && !product && mappedCustomDimensions.length === 0) {
      return undefined;
    }
    return {
      ...dims,
      ...(product ? { product } : {}),
      ...(mappedCustomDimensions.length > 0 ? { mappedCustomDimensions } : {}),
    };
  };

  // Merge the field dims by key over the inherited `customDimensions` (the
  // trigger spreads shallowly, so passing the bare array would clobber the base).
  // `undefined` when there are no fields, leaving the call unchanged.
  const productFieldOverride = useMemo<
    { customDimensions: CustomDimensions } | undefined
  >(() => {
    if (!entryFieldDimensions || entryFieldDimensions.length === 0) {
      return undefined;
    }
    const merged = new Map<string, { key: string; value: string }>();
    for (const dim of [
      ...(eventContext?.customDimensions ?? []),
      ...entryFieldDimensions,
    ]) {
      merged.set(dim.key, dim);
    }
    return { customDimensions: Array.from(merged.values()) };
  }, [eventContext?.customDimensions, entryFieldDimensions]);

  const flowId = currentStep?.flowId;
  const stepId = currentStep?.id;

  // -------------------------------------------------------------
  // Mount fire — entry: ElementLoaded + ProductLoaded (§6.1);
  //              non-entry: ElementLoaded (§7.3).
  //
  // Both branches stamp the dedup key as `ElementLoaded` — the wire
  // event name is the same, only the webElement shape (app-shell vs
  // step-scoped) differs. A given step is exclusively entry or
  // non-entry, so the (flowId|dslConfigId, stepId) scope already
  // partitions the keys per step instance — no collision risk.
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isActive) return;

    const keyCtx = { flowSessionId, flowId, dslConfigId, stepId };

    if (isEntryStep) {
      const elementLoadedKey = buildEntryPointKey('ElementLoaded', keyCtx);
      if (!hasFiredKey(elementLoadedKey)) {
        addFiredKey(elementLoadedKey);
        triggers.triggerElementLoadedEvent();
      }
      if (unitFiresProduct) {
        const productLoadedKey = buildEntryPointKey('ProductLoaded', keyCtx);
        if (!hasFiredKey(productLoadedKey)) {
          addFiredKey(productLoadedKey);
          triggers.triggerProductLoadedEvent(
            buildEntryProductOverride(productFieldOverride) as Parameters<
              typeof triggers.triggerProductLoadedEvent
            >[0],
          );
        }
      }
    } else {
      const elementLoadedKey = buildEntryPointKey('ElementLoaded', keyCtx);
      if (!hasFiredKey(elementLoadedKey)) {
        addFiredKey(elementLoadedKey);
        triggers.triggerStepElementLoadedEvent();
      }
    }
  }, [
    isActive,
    isEntryStep,
    unitFiresProduct,
    productFieldOverride,
    flowId,
    stepId,
    dslConfigId,
    flowSessionId,
    triggers,
  ]);

  // -------------------------------------------------------------
  // Intersect fire — entry: ProductViewed + FormViewed (§6.1);
  //                  non-entry: ElementViewed (§7.3).
  //
  // `FormViewed` is reserved for the entry step that introduces the
  // form context (it generates `formViewedCorrelationId` per §11.1);
  // non-entry steps fire the step-scoped `ElementViewed` instead,
  // mirroring the mount-fire wire-name parity.
  // -------------------------------------------------------------
  // Storage for the entry root. `useState` so a change re-runs the
  // observe effect; the `setEntryRootNode` callback identity is
  // pinned via `useCallback` with empty deps (underlying setter from
  // `useState` is already stable, but the wrap makes the contract
  // explicit and lets the body branch on the previous-node value
  // held in `entryRootRef`).
  const [entryRoot, setEntryRoot] = useState<HTMLElement | null>(null);
  const entryRootRef = useRef<HTMLElement | null>(null);

  const setEntryRootNode = useCallback((node: HTMLElement | null) => {
    // Repeated calls with the same non-null node (StrictMode same-
    // node attach, or a host re-render that does not actually move
    // the DOM element) are a no-op — re-setting state to the same
    // value would trigger an unnecessary observe-effect cycle.
    if (node === entryRootRef.current) return;
    entryRootRef.current = node;
    setEntryRoot(node);
  }, []);

  const { setNodes, entries, disconnect } = useIntersect();

  useEffect(() => {
    if (!isActive) return;
    // `setNodes` is the only handoff to `useIntersect`. When
    // `entryRoot` is `null` (host called `setEntryRootNode(null)` —
    // explicit detach, or StrictMode's dev-time double-effect bounce
    // before the re-attach), reset the observed list to `[]` so the
    // underlying observer disconnects from the previously observed
    // node. A subsequent `(null → node)` call repopulates the list
    // and the observer re-attaches to the new node.
    setNodes(entryRoot ? [entryRoot] : []);
  }, [isActive, entryRoot, setNodes]);

  useEffect(() => {
    if (!isActive) return;
    if (entries.length === 0) return;
    const intersecting = entries.find((entry) => entry.isIntersecting);
    if (!intersecting) return;

    // Disconnect synchronously before firing so any duplicate
    // intersect entry queued behind this one cannot re-enter the
    // body. Mirrors `Trackable.tsx`'s pattern; we intentionally do
    // NOT rely on `setNodes([])` for tear-down — under React 18
    // Strict Mode that pattern re-attaches and re-fires.
    disconnect();

    const keyCtx = { flowSessionId, flowId, dslConfigId, stepId };

    if (isEntryStep) {
      if (unitFiresProduct) {
        const productViewedKey = buildEntryPointKey('ProductViewed', keyCtx);
        if (!hasFiredKey(productViewedKey)) {
          addFiredKey(productViewedKey);
          triggers.triggerProductViewedEvent(
            buildEntryProductOverride(productFieldOverride) as Parameters<
              typeof triggers.triggerProductViewedEvent
            >[0],
          );
        }
      }
      const formViewedKey = buildEntryPointKey('FormViewed', keyCtx);
      if (!hasFiredKey(formViewedKey)) {
        addFiredKey(formViewedKey);
        triggers.triggerFormViewedEvent();
      }
    } else {
      const elementViewedKey = buildEntryPointKey('ElementViewed', keyCtx);
      if (!hasFiredKey(elementViewedKey)) {
        addFiredKey(elementViewedKey);
        triggers.triggerStepElementViewedEvent();
      }
    }
    // `entries` is the meaningful trigger; the other deps are stable
    // for a given step render and listing them keeps eslint happy
    // while staying behaviour-neutral.
  }, [
    isActive,
    isEntryStep,
    unitFiresProduct,
    productFieldOverride,
    entries,
    disconnect,
    flowId,
    stepId,
    dslConfigId,
    flowSessionId,
    triggers,
  ]);

  return { setEntryRootNode };
};
